import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

export function shapeEyebrow(
  svg,
  initX,
  initY,                 // Top of the arch
  params
) {
  // Destructure parameters with defaults
  const {
    w,
    h: hRect,            // Rectangle height (can be 0)
    archHeight,          // Height of arch (vertical radius)
    frameWidth = 10,
    frameColor = 'white',
    lineFrameColor = 'black',
    outerStrokeColor = 'black',
    outerStrokeWidth = 1,
    color = '#EDF9FF',   // Default glass color
    includeInnerTopLine = false,
    glassType = null,    // 'Tinted' | 'Textured' | null
    glassDesign = null  // e.g. 'Grey', 'Rain blast', etc.
  } = params;

  const rX = w / 2;
  const rY = archHeight;

  const left = initX;
  const right = initX + w;
  const centerX = initX + rX;
  const centerY = initY + rY;
  const bottom = initY + archHeight + hRect;

  const fw = frameWidth;

  // ====== Glass Fill (Base Layer) ======
  let baseFill = color;
  let usePattern = false;
  let tintOverlay = null;

  // TINTED GLASS
  if (glassType === 'Tinted') {
    baseFill = 'white'; // base is solid white, then tint overlays
    switch (glassDesign?.toLowerCase()) {
      case 'grey':
        tintOverlay = 'rgba(60, 60, 60, 0.2)';
        break;
      case 'bronze':
        tintOverlay = 'rgba(90, 50, 0, 0.2)';
        break;
      default:
        console.warn('Unknown tinted glass design:', glassDesign);
    }
  }

  // TEXTURED GLASS
  if (glassType === 'Textured' && glassDesign) {
    const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
    baseFill = `url(#${patternId})`;
    usePattern = true;
    defineTexturedGlassPatterns(svg, glassDesign);
    //defineTexturedGlassPatterns(svg, 'Rain blast');
  }

  // ====== Orientation Handling ======
  let pathOuter, pathInner;

 
    // Center orientation (horizontal division - top half)
    // === Outer path (frame) ===
    pathOuter = `
      M ${left - fw},${bottom + fw}
      L ${left - fw},${centerY}
      A ${rX + fw},${rY + fw} 0 0 1 ${right + fw},${centerY}
      L ${right + fw},${bottom + fw}
      Z
    `;

    svg.append('path')
      .attr('d', pathOuter)
      .attr('fill', frameColor)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);

    // === Inner path (shape fill) ===
    if (hRect > 0) {
      // Arch + rectangle
      pathInner = `
        M ${left},${bottom}
        L ${left},${centerY}
        A ${rX},${rY} 0 0 1 ${right},${centerY}
        L ${right},${bottom}
        Z
      `;
    } else {
      // Arch only
      pathInner = `
        M ${left},${centerY}
        A ${rX},${rY} 0 0 1 ${right},${centerY}
        L ${right},${centerY}
        L ${left},${centerY}
        Z
      `;
    }
  

  // === Base glass fill (pattern or solid white or default color) ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', baseFill)
    .attr('stroke', 'none');

  // === Tint overlay (semi-transparent) ===
  if (tintOverlay) {
    svg.append('path')
      .attr('d', pathInner)
      .attr('fill', tintOverlay)
      .attr('stroke', 'none');
  }

  // === Top stroke outline ===
  svg.append('path')
    .attr('d', pathInner)
    .attr('fill', 'none')
    .attr('stroke', outerStrokeColor)
    .attr('stroke-width', outerStrokeWidth);

  // === Optional inner rectangular top line ===
  if (includeInnerTopLine && hRect > 0) {
    svg.append('rect')
      .attr('x', left - frameWidth)
      .attr('y', centerY)
      .attr('width', w + 2 * frameWidth)
      .attr('height', frameWidth)
      .attr('fill', frameColor)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);
  }

  // === Calculate innerPoints for colonial grills ===
  const allPoints = [];
  const numPoints = 40; // More points for full ellipse

  // Generate points along the ellipse arc (top half)
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const angle = Math.PI * t; // 0 to PI (180 degrees for top half ellipse)
    const px = centerX + rX * Math.cos(Math.PI - angle);
    const py = centerY - rY * Math.sin(angle);
    allPoints.push([px, py]);
  }

  // Add bottom rectangle corners if rectangle portion exists
  if (hRect > 0) {
    allPoints.push([right, bottom]);
    allPoints.push([left, bottom]);
  }

  return allPoints;
}